import { useEffect } from 'react';
import * as z from 'zod';

import config from '@config';
import { configureEventLog, isOffline } from '@shared/events';
import { analyticsPreference } from '@shared/lib/reader-preferences';

import { createDestinations } from './destinations';
import { createDispatcher, type Dispatcher } from './dispatcher';

// A tenant's consent script (injected through pwa.json's preBody/postBody)
// can switch analytics off and on by posting one of these on the `analytics`
// BroadcastChannel, and can read `analytics_enabled` to see the result.
// Nothing in this app posts to the channel.
const CONSENT_CHANNEL = 'analytics';
const CONSENT_ENABLED_KEY = 'analytics_enabled';
const consentMessageSchema = z.enum(['enable', 'disable']);

let dispatcher: Dispatcher | undefined;
let consentRevoked = false;

function isSharingOn(): boolean {
	return analyticsPreference.get() === 'on' && !consentRevoked;
}

// Each event is stamped with this as it is recorded, so one recorded while
// sharing was off is never sent later.
configureEventLog({ isSharingOn });

function sync() {
	dispatcher ??= createDispatcher({
		destinations: createDestinations(config.analytics),
		isSharingOn,
		isOnline: () => !isOffline(),
	});
	dispatcher.sync();
	try {
		if (isSharingOn()) {
			localStorage.setItem(CONSENT_ENABLED_KEY, 'true');
		} else {
			localStorage.removeItem(CONSENT_ENABLED_KEY);
		}
	} catch {
		// Private mode etc.: the flag is informational.
	}
}

/**
 * Mounts the Analytics Dispatcher for the lifetime of the app and keeps it
 * in step with Settings → Privacy → Analytics. Off: no destination is
 * started and nothing is sent, and what is recorded meanwhile is never sent
 * later (index-template.ejs also skips loading the trackers it owns). Back
 * on: destinations start again now; the ones the page loads itself return
 * on the next launch.
 */
export function Analytics(): null {
	const [setting] = analyticsPreference.use();

	useEffect(() => {
		sync();
	}, [setting]);

	useEffect(() => {
		const channel = new BroadcastChannel(CONSENT_CHANNEL);
		const onMessage = ({ data }: MessageEvent) => {
			const message = consentMessageSchema.safeParse(data);
			if (!message.success) {
				return;
			}
			consentRevoked = message.data === 'disable';
			sync();
		};
		channel.addEventListener('message', onMessage);
		return () => {
			channel.removeEventListener('message', onMessage);
			channel.close();
		};
	}, []);

	return null;
}

/** Test-only: forget the dispatcher and any consent message. */
export function resetAnalyticsForTests(): void {
	dispatcher?.close();
	dispatcher = undefined;
	consentRevoked = false;
	configureEventLog({ isSharingOn });
}

/** Test-only: resolves when this tab has sent what was waiting. */
export function drainAnalyticsForTests(): Promise<void> {
	return dispatcher?.drain() ?? Promise.resolve();
}
